import { useFormikContext } from 'formik';
import startCase from 'lodash/startCase';
import React from 'react';

import { Box } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

import { Button, ButtonProps } from '../Button';
import {
  ModalFullPage, ModalFullPageActions, ModalFullPageContent, ModalFullPageTitle
} from '../ModalFullPage';
import { useIsMobile } from '@uplift/hooks';

export type EditableSectionRowFormWrapperProps = {
  children?: React.ReactNode;
  onCancel?: () => void;
  hideDesktopActions?: boolean;
  mobileModalTitle?: string;
  saveButtonText?: string;
  saveButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  actionsContent?: React.ReactNode;
  rowLabel: React.ReactNode;
  disableSaveOnInvalid?: boolean;
  desktopLeftActions?: React.ReactNode;
  noContentPadding?: boolean;
};

export const EditableSectionRowFormWrapper: React.FC<EditableSectionRowFormWrapperProps> = ({ children, ...props }) => {
  const isMobile = useIsMobile('sm');

  return isMobile ? (
    <MobileRowFormWrapper {...props}>
      {children}
    </MobileRowFormWrapper>
  ) : (
    <DesktopRowFormWrapper {...props}>
      {children}
    </DesktopRowFormWrapper>
  );
};

const MobileRowFormWrapper: React.FC<EditableSectionRowFormWrapperProps> = ({ children, onCancel, mobileModalTitle, saveButtonText = 'Save', saveButtonProps, cancelButtonProps, rowLabel = '', actionsContent, disableSaveOnInvalid, noContentPadding }) => {
  const formik = useFormikContext();
  const { classes } = useStyles();

  const title = React.useMemo(() => {
    if (typeof rowLabel === 'string') {
      return mobileModalTitle ?? `Edit ${startCase(rowLabel)}`;
    }

    return rowLabel;
  }, [ mobileModalTitle, rowLabel ]);

  const isSubmitDisabled = React.useMemo(() => {
    if (disableSaveOnInvalid) {
      return formik?.isSubmitting || !formik?.isValid;
    }

    return formik?.isSubmitting;
  }, [ disableSaveOnInvalid, formik?.isSubmitting, formik?.isValid ]);

  return (
    <ModalFullPage open transition>
      <ModalFullPageTitle displayMobileOnlyStyles>{title}</ModalFullPageTitle>
      <ModalFullPageContent
        id="modal-content"
        style={noContentPadding ? { padding: 0 } : {}}
      >
        {children}
      </ModalFullPageContent>
      <ModalFullPageActions displayMobileOnlyStyles showBottomDivider={!noContentPadding}>
        <Box width="100%">
          {actionsContent}
          <Box className={classes.buttonContainer}>
            <Button
              size="large"
              variant="outlined"
              disabled={formik?.isSubmitting}
              onClick={onCancel}
              {...cancelButtonProps}
            >
              Cancel
            </Button>
            <Button
              size="large"
              color="secondary"
              variant="contained"
              onClick={formik?.handleSubmit}
              {...saveButtonProps}
              disabled={isSubmitDisabled || saveButtonProps?.disabled}
              loading={formik?.isSubmitting || saveButtonProps?.loading}
            >
              {saveButtonText}
            </Button>
          </Box>
        </Box>
      </ModalFullPageActions>
    </ModalFullPage>
  );
};

const useStyles = makeStyles()((theme) => ({
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    '& > button': {
      flex: 1,
    }
  }
}));

export const DesktopRowFormWrapper: React.FC<EditableSectionRowFormWrapperProps> = ({ children, onCancel, hideDesktopActions, saveButtonText = 'Save', saveButtonProps, cancelButtonProps, actionsContent, disableSaveOnInvalid = true, desktopLeftActions }) => {
  const formik = useFormikContext();

  const isSubmitDisabled = React.useMemo(() => {
    if (disableSaveOnInvalid) {
      return formik?.isSubmitting || !formik?.isValid;
    }

    return formik?.isSubmitting;
  }, [ disableSaveOnInvalid, formik?.isSubmitting, formik?.isValid ]);

  return (
    <div>
      {children}
      {!hideDesktopActions && (
        <Box mt={3} mr={1} display="flex" flexDirection="column" alignItems="flex-end">
          {actionsContent && (
            <Box mb={2}>{actionsContent}</Box>
          )}
          <Box display="flex" alignItems="center" gap="8px" width="100%">
            <Box flex={1} display="flex" alignItems="center">
              {desktopLeftActions}
            </Box>
            <Button
              disabled={formik?.isSubmitting}
              onClick={onCancel}
              size="large"
              // needed so that click event is registered before dom rerenders from input onBlur
              onMouseDown={onCancel}
              testId="cancel-button"
              {...cancelButtonProps}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={formik?.handleSubmit}
              {...saveButtonProps}
              disabled={isSubmitDisabled || saveButtonProps?.disabled}
              loading={formik?.isSubmitting || saveButtonProps?.loading}
              testId="save-button"
            >
              {saveButtonText}
            </Button>
          </Box>
        </Box>
      )}
    </div>
  );
};
